import { Box, Button, Dialog, Fade, Slide, Typography } from "@mui/material";
import React, { FC, useCallback, useEffect } from "react";
import "./CustomPopup.scss";
import { Colors } from "../../styles/constant";
import useWindowSize from "../../hooks/useWindowSize";
import { tabViewPoint } from "../../utils/constants";
import { defaultEmptyFun } from "../../utils/helper";
import TriggerGTAEvent from "../../utils/GTA";
import { triggerCleverTapEvent } from "../../utils/CleverTap";
import { useAppSelector } from "../../redux-store/CustomReduxHooks";
import { E_ChannelOptions } from "../../globalTypes/GlobalTypes";
import { TransitionProps } from "@mui/material/transitions";

const FadeTransition = React.forwardRef(function FadeTransition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Fade ref={ref} {...props} />;
});

const SlideTransition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Shared across every CustomPopup instance so overlapping dialogs don't
// remove the background blur early when just one of them closes.
let openDialogPopupCount = 0;
const toggleBackgroundBlur = (shouldBlur: boolean) => {
  const root = document.getElementById("root");
  if (!root) return;
  openDialogPopupCount = Math.max(
    0,
    openDialogPopupCount + (shouldBlur ? 1 : -1),
  );
  root.classList.toggle(
    "popup-background-blur-active",
    openDialogPopupCount > 0,
  );
};

interface Props {
  open: boolean;
  handleOpen: Function;
  children?: React.ReactNode | null;
  btnStartIcon?: any;
  btnEndIcon?: any;
  btnLabel?: string;
  className?: string;
  anchor?: string;
  isDisabled?: boolean;
  flowType?: string;
  customeGetHelpHandler?: Function;
  screenType?: any;
  ScreenName?: any;
  transitionType?: "slide" | "fade";
  renderMode?: "dialog" | "inline";
  inlineOnDesktop?: boolean;
}

const CustomPopup: FC<Props> = ({
  open,
  handleOpen,
  children,
  btnLabel = "",
  btnStartIcon = null,
  btnEndIcon = null,
  className = "",
  anchor = "",
  isDisabled = false,
  flowType = "LI",
  customeGetHelpHandler = defaultEmptyFun,
  screenType = "",
  ScreenName = "",
  transitionType = "fade",
  renderMode = "dialog",
  inlineOnDesktop = false,
}) => {
  const { width } = useWindowSize();
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const isSuperApp = csAppSlice?.channel === E_ChannelOptions.SUPER_APP;
  const shouldRenderInline =
    open &&
    (renderMode === "inline" || (inlineOnDesktop && width > tabViewPoint));

  const isDialogOpen = open && !shouldRenderInline;
  useEffect(() => {
    if (!isDialogOpen) return;
    toggleBackgroundBlur(true);
    return () => toggleBackgroundBlur(false);
  }, [isDialogOpen]);

  const handleOpenWrapper = useCallback(() => {
    if (btnLabel === "Close") {
      triggerCleverTapEvent("close_click", {
        ScreenType: screenType,
        ScreenName: ScreenName,
        isPrevPageNameRequired: true,
      });

      TriggerGTAEvent("close_click", {
        CTA_text: "Close",
        screen_type: "hi_servicing",
        screen_name: "health_insurance_servicing_detail_screen",
      });
    } else if (btnLabel === "Go Back") {
      triggerCleverTapEvent("back_click", {
        ScreenType: screenType,
        ScreenName: ScreenName,
        isPrevPageNameRequired: true,
      });
    }
    handleOpen();
  }, []);

  const buttonComponent = (
    <Box
      display="flex"
      justifyContent="center"
      marginTop={anchor === "bottom" ? "1rem" : ""}
    >
      <Button
        className={`custom-drawer-btn ${
          flowType === "HL" ? "custom-drawer-btn-red" : ""
        }`}
        onClick={() =>
          anchor && anchor === "top" && flowType === "HL"
            ? customeGetHelpHandler()
            : handleOpenWrapper()
        }
        disabled={isDisabled}
      >
        <Box display="flex" alignItems="center">
          {btnStartIcon ? btnStartIcon : null}
          <Typography
            marginLeft=".3125rem"
            marginRight=".3125rem"
            variant="h5"
            color={Colors.red}
          >
            {btnLabel}
          </Typography>
          {btnEndIcon ? btnEndIcon : null}
        </Box>
      </Button>
    </Box>
  );

  if (shouldRenderInline) {
    return (
      <Box
        className={`custom-popup-inline ${
          isSuperApp ? "no-tap-highlights" : ""
        } ${className}`}
      >
        <Box className="popup-content-wrapper">{children}</Box>
      </Box>
    );
  }

  return (
    <Dialog
      fullWidth
      className={`custom-popup ${
        width > tabViewPoint ? "customPopCentered" : ""
      } ${isSuperApp ? "no-tap-highlights" : ""} ${className}`}
      open={open}
      TransitionComponent={
        transitionType === "slide" ? SlideTransition : FadeTransition
      }
    >
      {btnLabel && buttonComponent}
      <Box className="popup-content-wrapper">{children}</Box>
    </Dialog>
  );
};

export default CustomPopup;
